import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Divider,
  Button,
  useTheme,
  type Theme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  School as DomainsIcon,
  Class as SubjectsIcon,
  Assignment as SurveysIcon,
  QuestionAnswer as ResponsesIcon,
  CheckCircle as CompletionIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserRole } from '@features/authentication/types';
import type { SummaryDTO } from '@lecturo/server/features/insight/dtos';

interface QuickStatsContentProps {
  summary: SummaryDTO;
  currentRole: UserRole;
  isLoading?: boolean;
}

interface StatItem {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
}

function resolvePaletteColor(theme: Theme, path: string): string {
  const keys = path.split('.');
  let current: unknown = theme.palette;
  for (const key of keys) {
    if (current !== null && typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return path;
    }
  }
  return typeof current === 'string' ? current : path;
}

/**
 * Determines which stat items to display based on the user's role.
 *
 * - UNIVERSITY_ADMIN: Institutes, Subjects, Surveys, Responses, Completion Rate
 * - FACULTY_ADMIN: Subjects, Surveys, Responses, Completion Rate
 * - PROFESSOR: Surveys, Responses, Completion Rate
 */
function getStatItems(summary: SummaryDTO, role: UserRole, t: (key: string) => string): StatItem[] {
  const items: StatItem[] = [];

  if (role === UserRole.UNIVERSITY_ADMIN) {
    items.push({
      label: t('insight.summary.domains'),
      value: summary.domainCount.toLocaleString(),
      icon: DomainsIcon,
      color: 'primary.main',
    });
  }

  if (role === UserRole.UNIVERSITY_ADMIN || role === UserRole.FACULTY_ADMIN) {
    items.push({
      label: t('insight.summary.subjects'),
      value: summary.subjectCount.toLocaleString(),
      icon: SubjectsIcon,
      color: 'secondary.main',
    });
  }

  items.push({
    label: t('insight.summary.surveys'),
    value: summary.surveyCount.toLocaleString(),
    icon: SurveysIcon,
    color: 'primary.dark',
  });

  items.push({
    label: t('insight.summary.responses'),
    value: summary.responseCount.toLocaleString(),
    icon: ResponsesIcon,
    color: 'primary.light',
  });

  items.push({
    label: t('insight.summary.completionRate'),
    value: `${summary.completionRate.toFixed(1)}%`,
    icon: CompletionIcon,
    color: 'success.main',
  });

  return items;
}

/**
 * Compact horizontal stat bar for the dashboard.
 * Displays role-appropriate analytics summary with a link to the full statistics page.
 */
export const QuickStatsContent: React.FC<QuickStatsContentProps> = ({
  summary,
  currentRole,
  isLoading,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const statItems = getStatItems(summary, currentRole, t);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t('dashboard.quickStats.title')}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              ({t('dashboard.quickStats.allTime')})
            </Typography>
          </Box>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/app/insight')}
            sx={{ textTransform: 'none' }}
          >
            {t('dashboard.quickStats.viewAll')}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {statItems.map((stat, index) => (
            <React.Fragment key={stat.label}>
              {index > 0 && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: 'none', sm: 'block' } }}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  minWidth: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: alpha(resolvePaletteColor(theme, stat.color), 0.08),
                  }}
                >
                  <stat.icon sx={{ fontSize: 18, color: stat.color }} />
                </Box>
                {isLoading ? (
                  <Skeleton variant="text" width={50} height={28} />
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                    {stat.value}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {stat.label}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
